import styles from '../_signup.module.scss'
import { useMutation } from '@apollo/client'
import {
  CardNumberElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import classnames from 'classnames'
import router from 'next/router'
import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useToasts } from '../../../components/complex/Toast'
import { SplashScreenPure } from '../../../components/SplashScreen'
import { t } from '../../../utils'
import { useAuth } from '../../../utils/auth'
import Account from './Account'
import Payment from './Payment'
import Plan from './Plan'
import PendingInvitation from './PendingInvitation'
import {
  errorMessage as extractErrorMessage,
  signUpErrorMessage
} from '../../../utils/errorMessage'
import Image from 'next/legacy/image'
import logoImg from '@public/images/logo_wave.svg'
import {
  SIGN_UP_GIFT_SUBSCRIPTION_MUTATION,
  SIGN_UP_MUTATION
} from '../../../gql'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { Plan as PlanType, User } from '../../../__generated__/graphql'
import useEmailCheck from '../../../utils/signup/useEmailCheck'
import { isLabEnabled } from '../../../utils/isDevUser'
import { metaPixelEvent } from '../../../lib/fpixel'
import { labSignupPath } from '../../../utils/routeHelpers'
import Link from 'next/link'
import { LogoHeader } from '../../../components/LogoHeader'
import { useTheme } from 'next-themes'

const FormTab = ({ children }) => (
  <div className={styles.signupTab}>
    <span></span>
    {children}
  </div>
)
const formTabs: string[] = ['Account', 'Plan', 'Payment']

interface CheckoutFormType {
  plan: PlanType
  email?: string
  token?: string
  firstName?: string
  lastName?: string
  overrideInvitation?: boolean
}

const CheckoutForm: React.FC<CheckoutFormType> = ({
  plan,
  email = null,
  token = null,
  firstName = null,
  lastName = null,
  overrideInvitation = false
}) => {
  const { addToast } = useToasts()
  const { theme } = useTheme()

  const [activeIndex, setActiveIndex] = useState({ activeIndex: 0 })
  const [indexToChange, setIndexToChange] = useState(0)
  const [showPendingInvitation, setShowPendingInvitation] = useState(false)
  const [invitationData, setInvitationData] = useState<any>(null)
  const [overridePendingInvitation, setOverridePendingInvitation] =
    useState(overrideInvitation)
  const {
    handleSubmit,
    register,
    getValues,
    formState: { errors },
    setError,
    setValue,
    watch
  } = useForm({
    mode: 'all',
    shouldUnregister: false,
    defaultValues: {
      firstName: firstName || '',
      lastName: lastName || '',
      email: email || '',
      password: '',
      passwordConfirm: '',
      organization: '',
      zipCode: ''
    }
  })
  const emailInput = watch('email')
  const couponCode = watch('couponCode')
  const isGift = !!token
  const elements = !isGift ? useElements() : null
  const stripe = !isGift ? useStripe() : null
  const password = useRef({})
  password.current = watch('password', '')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const { setCurrentUser } = useAuth()

  useEffect(() => {
    if (isGift) {
      setValue('email', email)
      setValue('firstName', firstName)
      setValue('lastName', lastName)
    } else if (email && overrideInvitation) {
      setValue('email', email)
    }
  }, [email, overrideInvitation, isGift, firstName, lastName, setValue])

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      if (!('zipCode' in errors) && activeIndex.activeIndex !== 0)
        setActiveIndex({ activeIndex: 0 })
    }
  }, [errors])

  const { executeRecaptcha } = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
    ? useGoogleReCaptcha()
    : { executeRecaptcha: null }

  const handleReCaptchaVerify = useCallback(async () => {
    if (executeRecaptcha) {
      const token = await executeRecaptcha('signUp')
      return token
    } else {
      return null
    }
  }, [executeRecaptcha])

  const signUpErrorsAdaptation = (error: string) => {
    if (error.match(/email/)) {
      setError('email', { type: 'server' })
    }
    if (error.match(/password/)) {
      setError('password', { type: 'server' })
    }

    return signUpErrorMessage(error)
  }

  const [createUser] = useMutation(SIGN_UP_MUTATION, {
    onError: (error) => {
      setIsLoading(false)
      setIsSubmitted(false)
      const message = extractErrorMessage(error)
      addToast(signUpErrorsAdaptation(message), 'danger')
      setActiveIndex({ activeIndex: 0 })
    },
    update: (_, result) => {
      const user = { ...result.data.signUp.user }
      setCurrentUser(user as User)
    },
    onCompleted: (data) => {
      if (!plan.isFree && !plan.isGift) {
        const eventID = data.signUp.user.id + '_start_trial'
        metaPixelEvent(
          'start_trial',
          {
            em: data.signUp.user.email,
            fn: data.signUp.user.firstName,
            ln: data.signUp.user.lastName,
            zp: getValues().zipCode,
            external_id: data.signUp.user.id,
            content_category: 'Subscription',
            content_type: 'product',
            content_ids: [plan?.id],
            content_name: plan?.name || 'not-set'
          },
          { eventID }
        )
      }

      setIsLoading(false)

      const user = data.signUp.user as User

      if (isLabEnabled(user)) {
        router.push(labSignupPath() + '?welcome')
      } else {
        router.push('/welcome')
      }
    }
  })

  const [createUserWithGiftSubscription] = useMutation(
    SIGN_UP_GIFT_SUBSCRIPTION_MUTATION,
    {
      onError: (error) => {
        setIsLoading(false)
        setIsSubmitted(false)
        const message = extractErrorMessage(error)
        addToast(signUpErrorsAdaptation(message), 'danger')
        setActiveIndex({ activeIndex: 0 })
      },
      update: (_, result) => {
        const user = { ...result.data.signUpGiftSubscription.user }
        setCurrentUser(user as User)
      },
      onCompleted: (data) => {
        const eventID = data.signUpGiftSubscription.user.id + '_gift_signup'
        metaPixelEvent(
          'gift_signup',
          {
            em: data.signUpGiftSubscription.user.email,
            fn: data.signUpGiftSubscription.user.firstName,
            ln: data.signUpGiftSubscription.user.lastName,
            zp: getValues().zipCode,
            external_id: data.signUpGiftSubscription.user.id,
            content_category: 'GiftSignup',
            content_type: 'product',
            content_ids: [plan?.id],
            content_name: plan?.name || 'not-set'
          },
          { eventID }
        )
        setIsLoading(false)
        router.push('/welcome')
      }
    }
  )

  const performCreate = async (recaptchaToken: string) => {
    setIsLoading(true)
    const values = getValues()
    let payload

    if (isGift) {
      createUserWithGiftSubscription({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          organization: values.organization,
          giftSubToken: token
        }
      })
    } else {
      const cardElement = elements.getElement(CardNumberElement)
      const name = [values.firstName, values.lastName]
        .filter((v) => Boolean(v))
        .join(' ')
      payload = await stripe.createToken(cardElement, {
        name,
        address_zip: values.zipCode
      })
      if (payload.error) {
        setIsSubmitted(false)
        setIsLoading(false)
      } else {
        createUser({
          variables: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
            organization: values.organization,
            planId: plan.id,
            zipCode: `${values.zipCode}`,
            couponCode: values.couponCode,
            stripeCardToken: payload.token?.id || '',
            recaptchaToken: recaptchaToken,
            overridePendingInvitation: overridePendingInvitation
          }
        })
      }
    }
  }

  const handleContinue = () => {
    setActiveIndex((prevIndex) => {
      return { activeIndex: prevIndex.activeIndex + 1 }
    })
  }
  const handleTabSwitch = (index) => {
    setActiveIndex({ activeIndex: index })
  }

  const onSubmit = async (_, event) => {
    setIsSubmitted(true)
    event.preventDefault()

    const recaptchaToken = await handleReCaptchaVerify()

    if (!isGift && (!stripe || !elements)) {
      setIsSubmitted(false)
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return
    }

    performCreate(recaptchaToken)
  }

  const handleTabClick = async (index) => {
    setIndexToChange(index)
    const prevIndex = activeIndex.activeIndex
    //check email if switch from the first tab
    if (prevIndex === 0) {
      await checkEmailAndSwitchTab()
    } else setActiveIndex({ activeIndex: index })
  }

  const checkEmailAndSwitchTab = useEmailCheck({
    email: emailInput || '',
    onContinue: () => handleTabSwitch(indexToChange),
    onPendingInvitation: (data) => {
      setInvitationData(data)
      setShowPendingInvitation(true)
    },
    overrideInvitation: overridePendingInvitation
  })

  const checkEmailAndContiune = useEmailCheck({
    email: emailInput || '',
    onContinue: handleContinue,
    onPendingInvitation: (data) => {
      setInvitationData(data)
      setShowPendingInvitation(true)
    },
    overrideInvitation: overridePendingInvitation
  })

  // Show either Account or PendingInvitation in the first step
  const accountPanel = useMemo(() => {
    if (showPendingInvitation) {
      return (
        <PendingInvitation
          key="PendingInvitation"
          email={emailInput || ''}
          invitationOrganization={invitationData?.invitationOrganization}
          onSignUpAnyway={() => {
            setShowPendingInvitation(false)
            setOverridePendingInvitation(true)
            handleContinue()
          }}
        />
      )
    }
    return (
      <Account
        key="Account"
        register={register}
        errors={errors}
        password={password}
        handleContinue={checkEmailAndContiune}
      />
    )
  }, [
    showPendingInvitation,
    errors,
    password,
    checkEmailAndContiune,
    emailInput,
    invitationData
  ])

  const planPanel = useMemo(
    () => <Plan key="Plan" plan={plan} handleContinue={handleContinue} />,
    [plan]
  )

  const paymentPanel = useMemo(
    () => (
      <Payment
        key="Payment"
        register={register}
        couponCode={couponCode}
        planId={plan.id}
        errors={errors}
        isSubmitted={isSubmitted}
      />
    ),
    [couponCode, errors, isSubmitted, plan.id]
  )

  return (
    <>
      <div className={styles.signupContainer}>
        <div className={styles.logoContainer}>
          <Link href={'/'} prefetch={false}>
            <LogoHeader width={190} height={45} theme={theme} />
          </Link>
          <div className={styles.signupLogo}>
            <Image src={logoImg} alt="Worship Online" width={100} height={77} />
          </div>
        </div>
        <p className={styles.signupTitle}>
          {t(`Create Your ${!isGift ? 'Free' : ''} Account Now`)}
        </p>
        {!isGift && (
          <p className={styles.signupSubtitle}>
            {t(
              `First ${
                plan.isCustom ? '30' : '14'
              } days free, no contracts, downgrade or cancel your account at any time`
            )}
          </p>
        )}
        <form className={styles.signupMain} onSubmit={handleSubmit(onSubmit)}>
          {!isGift ? (
            <>
              <div className={styles.signupTabs}>
                {formTabs.map((tab, index) => {
                  const className =
                    activeIndex.activeIndex >= index ? styles.activeTab : ''

                  return (
                    <div
                      key={index}
                      onClick={() => handleTabClick(index)}
                      className={className}
                    >
                      <FormTab>{tab}</FormTab>
                    </div>
                  )
                })}
              </div>
              <div className={styles.signupPanels}>
                <div
                  className={
                    activeIndex.activeIndex === 0 ? styles.activePanel : ''
                  }
                >
                  {accountPanel}
                </div>
                <div
                  className={
                    activeIndex.activeIndex === 1 ? styles.activePanel : ''
                  }
                >
                  {planPanel}
                </div>
                <div
                  className={
                    activeIndex.activeIndex === 2 ? styles.activePanel : ''
                  }
                >
                  {paymentPanel}
                </div>
              </div>
            </>
          ) : (
            <div
              className={classnames(styles.signupPanels, styles.activePanel)}
            >
              <Account
                key="Account"
                register={register}
                errors={errors}
                password={password}
                isSubmitted={isSubmitted}
              />
            </div>
          )}
          {isLoading && <SplashScreenPure />}
        </form>
      </div>
    </>
  )
}

export default CheckoutForm
